import { LocalizedStringRecord } from "../../app/slices/TrainingLocalizationSlice";
import { TranslationScope } from "../../app/slices/GlobalSlice";

// Defined with the state that holds it, re-exported here so the feature's
// callers keep importing it alongside the helpers that use it.
export type { TranslationScope };

/**
 * A text counts as missing when it is absent, empty, or whitespace. This is the
 * predicate the translate-missing filter has always applied; it lives here so the
 * count shown next to a language and the work actually sent can never disagree.
 */
export const isMissing = (
  entries: LocalizedStringRecord,
  key: string,
  language: string
) => readValue(entries, key, language).trim() === "";

/**
 * Entries are not guaranteed to carry every language for every key, so reading
 * through them blindly throws once the source language is editor-chosen.
 */
export const readValue = (
  entries: LocalizedStringRecord,
  key: string,
  language: string
) => entries[key]?.[language]?.value ?? "";

/** The keys a run would actually send for one target language. */
export const keysToSend = (
  entries: LocalizedStringRecord,
  keys: string[],
  language: string,
  scope: TranslationScope
) =>
  scope === "missing"
    ? keys.filter((key) => isMissing(entries, key, language))
    : keys;

/**
 * Total texts a run will send, summed across the selected languages — the number
 * on the action button. Not a per-language figure: two languages with four gaps
 * each is eight texts, and the request count is what the editor is authorising.
 */
export const countTextsToSend = (
  entries: LocalizedStringRecord,
  keys: string[],
  languages: string[],
  scope: TranslationScope
) =>
  languages.reduce(
    (total, language) =>
      total + keysToSend(entries, keys, language, scope).length,
    0
  );
